/**
 * v109.9.178 — File-upload helpers backed by Emergent Object Storage.
 *
 * The backend exposes:
 *   • `POST   /api/uploads?purpose=<tag>&ref_id=<optional>` — multipart
 *   • `GET    /api/uploads/{id}?auth=<jwt>` — inline download / <img src>
 *   • `DELETE /api/uploads/{id}` — soft delete (only uploader / master)
 *
 * `uploadFile()` returns { id, url, path, size, content_type,
 * original_filename, purpose }. Store the whole object (or just `id`) on
 * the parent record — never store the raw storage_path client-side.
 */
import api from "./api";

export const MAX_UPLOAD_MB = 20;

/**
 * Upload a single File instance to object storage.
 *
 * @param {File}   file      The browser File instance from an <input type=file>.
 * @param {Object} [opts]
 * @param {string} [opts.purpose]  Tag stored on the row (default "general").
 * @param {string} [opts.ref_id]   Related record id (e.g. settlement id).
 * @param {(pct:number)=>void} [opts.onProgress] Optional progress callback (0-100).
 * @returns {Promise<{id:string,url:string,path:string,size:number,content_type:string,original_filename:string,purpose:string}>}
 */
export async function uploadFile(file, { purpose = "general", ref_id = "", onProgress } = {}) {
  if (!file) throw new Error("No file provided");
  if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
    throw new Error(`File exceeds ${MAX_UPLOAD_MB} MB limit`);
  }
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, {
    params: { purpose, ref_id },
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (!onProgress || !evt.total) return;
      onProgress(Math.round((evt.loaded / evt.total) * 100));
    },
  });
  return data;
}

/**
 * Build an <img>-safe URL for a stored upload. Uses query-param auth so
 * the browser can render the image without extra JS plumbing.
 * @param {string|{id:string}} idOrRecord — upload id or the record returned by uploadFile.
 * @param {string} token — the current JWT (from AuthContext).
 * @returns {string} — full URL ready for <img src>.
 */
export function fileURL(idOrRecord, token) {
  const id = typeof idOrRecord === "string" ? idOrRecord : idOrRecord?.id;
  if (!id) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const q = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${base}/api/uploads/${id}${q}`;
}

/**
 * Soft-delete an uploaded file. Only the uploader or Master Admin can do it.
 */
export async function deleteFile(id) {
  if (!id) return;
  await api.delete(`/uploads/${id}`);
}

export async function openUpload(id) {
  const win = window.open("", "_blank");
  const { data } = await api.get(`/uploads/${id}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  if (win) win.location.href = url; else window.open(url, "_blank");
}
